import '@/App.css';
import { lazy } from 'react';
import PageLoadingBoundary from './components/PageLoadingBoundary';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SustainabilityRoute } from './components/SustainabilityRoute';
import Login from './pages/Login';
import { isAuthenticated } from './lib/auth';

// Download feature pages only when their route is opened. Login stays in the initial bundle.
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
// Self-Serve signup + OAuth journey (Phase 1)
const Signup = lazy(() => import('./pages/auth/Signup'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));
const TryExtract = lazy(() => import('./pages/try/TryExtract'));
const TryBillView = lazy(() => import('./pages/try/TryBillView'));
// Billing / Subscriptions (Phase 2)
const PlanPicker = lazy(() => import('./pages/billing/PlanPicker'));
const BillingHome = lazy(() => import('./pages/billing/BillingHome'));
const SubscriptionSuccess = lazy(() => import('./pages/billing/SubscriptionSuccess'));
// ── Self-Serve foundation demo (Module 1) ────────────────────────────
const TemplateDemoPage = lazy(() => import('./pages/_demo/TemplateDemoPage'));
const StatsBoard = lazy(() => import('./pages/StatsBoard'));
const BillJobs = lazy(() => import('./pages/BillJobs'));
const KpiDashboard = lazy(() => import('./pages/intake/KpiDashboard'));
const BulkDownloads = lazy(() => import('./pages/BulkDownloads'));
const Viewer = lazy(() => import('./pages/Viewer'));
const ConsolidatedViewer = lazy(() => import('./pages/ConsolidatedViewer'));
const EmissionsReport = lazy(() => import('./pages/sustainability/EmissionsReport'));
const DemandReport = lazy(() => import('./pages/reports/DemandReport'));
const Settings = lazy(() => import('./pages/Settings'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const AIManagerPage = lazy(() => import('./pages/AIManagerPage'));
const Cockpit = lazy(() => import('./pages/Cockpit'));
const UbmLocations = lazy(() => import('./pages/ubm/Locations'));
const OnboardingPlannerPage = lazy(() => import('./pages/ubm/OnboardingPlannerPage'));
const UbmSpaces = lazy(() => import('./pages/ubm/Spaces'));
const UbmMeters = lazy(() => import('./pages/ubm/Meters'));
const UbmVendorAccounts = lazy(() => import('./pages/ubm/VendorAccounts'));
const EntitiesPage = lazy(() => import('./pages/ubm/EntitiesPage'));
const OnboardEntities = lazy(() => import('./pages/ubm/OnboardEntities'));
const PaymentVerification = lazy(() => import('./pages/PaymentVerificationV2'));
const PaymentFileView = lazy(() => import('./pages/PaymentFileViewV2'));
const PaymentConfig = lazy(() => import('./pages/PaymentConfig'));
const PaymentsV2Page = lazy(() => import('./pages/payments/PaymentsV2Page'));
const VendorACHPanel = lazy(() => import('./pages/payments/VendorACHPanel'));
const PaymentsBillsWorkspace = lazy(() => import('./pages/payments/PaymentsBillsWorkspace'));
const ServiceFeeReport = lazy(() => import('./pages/payments/ServiceFeeReport'));
const VarianceReport = lazy(() => import('./pages/payments/VarianceReport'));
const RefundsReport = lazy(() => import('./pages/payments/RefundsReport'));
const LateFeeReport = lazy(() => import('./pages/payments/LateFeeReport'));
const EmergencyBillUpload = lazy(() => import('./pages/payments/EmergencyBillUpload'));
const UploadCreditFilePage = lazy(() => import('./pages/payments/UploadCreditFilePage'));
const CreditDebitReportPage = lazy(() => import('./pages/payments/CreditDebitReportPage'));
const GlobalCreditDebitPage = lazy(() => import('./pages/payments/GlobalCreditDebitPage'));
const Bill2RollinPage = lazy(() => import('./pages/ubm/Bill2RollinPage'));
const MigrationJourneysPage = lazy(() => import('./pages/ubm/MigrationJourneysPage'));
const MigrationJourneyDetail = lazy(() => import('./pages/ubm/MigrationJourneyDetail'));
const PropertyDetailsPage = lazy(() => import('./pages/ubm/PropertyDetailsPage'));
const PropertyEditPage = lazy(() => import('./pages/ubm/PropertyEditPage'));
const Bill2RollinEditPage = lazy(() => import('./pages/ubm/Bill2RollinEditPage'));
const CreditsSuccess = lazy(() => import('./pages/credits/CreditsSuccess'));
const CreditsCancel = lazy(() => import('./pages/credits/CreditsCancel'));
const CreditsUsagePage = lazy(() => import('./pages/credits/CreditsUsagePage'));
const DevToolsPage = lazy(() => import('./pages/superadmin/DevToolsPage'));
const CreditsPricingPage = lazy(() => import('./pages/superadmin/CreditsPricingPage'));
const IntakeRollupPage = lazy(() => import('./pages/superadmin/IntakeRollupPage'));
const ValidationReport = lazy(() => import('./pages/intake/ValidationReport'));
const MissingBills = lazy(() => import('./pages/intake/MissingBills'));
const EnergyStarReport = lazy(() => import('./pages/energyStar/EnergyStarReport'));
const PaasDashboard = lazy(() => import('./pages/paas/PaasDashboard'));
const PaasAccounts = lazy(() => import('./pages/paas/PaasAccounts'));
const PaasOnboard = lazy(() => import('./pages/paas/PaasOnboard'));
const PaymentsDashboard = lazy(() => import('./pages/payments/PaymentsDashboard'));
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
const SyncJobDetail = lazy(() => import('./pages/ubm/SyncJobDetail'));
const JobRunDetail = lazy(() => import('./pages/ubm/JobRunDetail'));
const BillsPage = lazy(() => import('./pages/bills/BillsPage'));
const BillViewPage = lazy(() => import('./pages/bills/BillViewPage'));
const BillsDashboardPage = lazy(() => import('./pages/bills/BillsDashboardPage'));
const OrgDashboardPage = lazy(() => import('./pages/dashboard/OrgDashboardPage'));
const MyOrganizationsDashboard = lazy(() => import('./pages/dashboard/MyOrganizationsDashboard'));
const ContractsListPage = lazy(() => import('./pages/procurement/ContractsListPage'));
const ContractBillsReportPage = lazy(() => import('./pages/procurement/ContractBillsReportPage'));
const ContractDetailPage = lazy(() => import('./pages/procurement/ContractDetailPage'));
const ContractsDashboardPage = lazy(() => import('./pages/procurement/ContractsDashboardPage'));
const BudgetsListPage = lazy(() => import('./pages/procurement/BudgetsListPage'));
const BudgetDetailPage = lazy(() => import('./pages/procurement/BudgetDetailPage'));
const BudgetReportPage = lazy(() => import('./pages/procurement/BudgetReportPage'));
// AI Agent launcher now lives inside MainLayout's top nav (see AiAgentNavLauncher).

function App() {
  return (
    <>
      <BrowserRouter>
        <PageLoadingBoundary>
        <Routes>
          <Route path="/login" element={isAuthenticated() ? <Navigate to="/dashboard" /> : <Login />} />
          <Route path="/signup" element={isAuthenticated() ? <Navigate to="/dashboard" /> : <Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/try" element={<TryExtract />} />
          <Route path="/try/bill/:documentId" element={<TryBillView />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
          <Route path="/billing" element={<ProtectedRoute><BillingHome /></ProtectedRoute>} />
          <Route path="/billing/choose" element={<ProtectedRoute><PlanPicker /></ProtectedRoute>} />
          <Route path="/billing/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <OrgDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-organizations"
            element={<ProtectedRoute><MyOrganizationsDashboard /></ProtectedRoute>}
          />
          <Route
            path="/dashboard/legacy"
            element={
              <ProtectedRoute>
                <StatsBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bill-jobs"
            element={
              <ProtectedRoute>
                <BillJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-hub/kpi-dashboard"
            element={
              <ProtectedRoute>
                <KpiDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-hub/validation-report"
            element={
              <ProtectedRoute>
                <ValidationReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-hub/missing-bills"
            element={<Navigate to="/bills/missing" replace />}
          />
          <Route
            path="/bills/missing"
            element={
              <ProtectedRoute>
                <MissingBills />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/energy-star"
            element={
              <ProtectedRoute>
                <EnergyStarReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/intake-rollup"
            element={
              <ProtectedRoute>
                <IntakeRollupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-downloads"
            element={
              <ProtectedRoute>
                <BulkDownloads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/viewer/:documentId"
            element={
              <ProtectedRoute>
                <Viewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sustainability/emissions"
            element={
              <SustainabilityRoute>
                <EmissionsReport />
              </SustainabilityRoute>
            }
          />
          <Route
            path="/consolidated-viewer/:documentId"
            element={
              <ProtectedRoute>
                <ConsolidatedViewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/ai-manager"
            element={
              <ProtectedRoute>
                <AIManagerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cockpit"
            element={
              <ProtectedRoute>
                <Cockpit />
              </ProtectedRoute>
            }
          />
          {/* UBM Routes */}
          <Route
            path="/procurement/dashboard"
            element={
              <ProtectedRoute>
                <ContractsDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/contracts"
            element={
              <ProtectedRoute>
                <ContractsListPage />
              </ProtectedRoute>
            }
          />
          <Route path="/procurement/contract-bills-report" element={<ProtectedRoute><ContractBillsReportPage /></ProtectedRoute>} />
          <Route
            path="/procurement/contracts/:contractId"
            element={
              <ProtectedRoute>
                <ContractDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/budgets"
            element={
              <ProtectedRoute>
                <BudgetsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/budgets/:budgetId"
            element={
              <ProtectedRoute>
                <BudgetDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/budgets/:budgetId/report"
            element={
              <ProtectedRoute>
                <BudgetReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/onboarding-planner"
            element={
              <ProtectedRoute>
                <OnboardingPlannerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/properties"
            element={
              <ProtectedRoute>
                <UbmLocations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/units"
            element={
              <ProtectedRoute>
                <UbmSpaces />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/meters"
            element={
              <ProtectedRoute>
                <UbmMeters />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/vendors"
            element={
              <ProtectedRoute>
                <UbmVendorAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/migration"
            element={
              <ProtectedRoute>
                <MigrationJourneysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/migration/:journeyId"
            element={
              <ProtectedRoute>
                <MigrationJourneyDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/onboard-entities"
            element={
              <ProtectedRoute>
                <OnboardEntities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/entities"
            element={
              <ProtectedRoute>
                <EntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin"
            element={
              <ProtectedRoute>
                <Bill2RollinPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/property/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/edit/:accountIdRef"
            element={
              <ProtectedRoute>
                <Bill2RollinEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/edit-property/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyEditPage />
              </ProtectedRoute>
            }
          />
          {/* Payments V2 Routes */}
          <Route
            path="/payments/dashboard"
            element={
              <ProtectedRoute>
                <PaymentsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/bills"
            element={
              <ProtectedRoute>
                <PaymentsBillsWorkspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/report"
            element={
              <ProtectedRoute>
                <PaymentsDashboard isGlobal={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/workspace"
            element={
              <ProtectedRoute>
                <PaymentsBillsWorkspace isGlobal={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <PaymentsV2Page />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/vendor-ach"
            element={
              <ProtectedRoute>
                <VendorACHPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/service-fees"
            element={
              <ProtectedRoute>
                <ServiceFeeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/variance"
            element={
              <ProtectedRoute>
                <VarianceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/refunds-report"
            element={
              <ProtectedRoute>
                <RefundsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/late-fee-report"
            element={
              <ProtectedRoute>
                <LateFeeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/emergency-bill-upload"
            element={
              <ProtectedRoute>
                <EmergencyBillUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/credit-debit"
            element={
              <ProtectedRoute>
                <CreditDebitReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/upload-credit-file"
            element={
              <ProtectedRoute>
                <UploadCreditFilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/dev-tools"
            element={
              <ProtectedRoute>
                <DevToolsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/credits-pricing"
            element={
              <ProtectedRoute>
                <CreditsPricingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/global-credit-debit"
            element={
              <ProtectedRoute>
                <GlobalCreditDebitPage />
              </ProtectedRoute>
            }
          />
          {/* Invoices Route */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
          {/* Bills Module Routes */}
          <Route
            path="/bills"
            element={
              <ProtectedRoute>
                <BillsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/dashboard"
            element={
              <ProtectedRoute>
                <BillsDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/demand"
            element={
              <ProtectedRoute>
                <DemandReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/:billId"
            element={
              <ProtectedRoute>
                <BillViewPage />
              </ProtectedRoute>
            }
          />
          {/* PaaS (Parsing-as-a-Service) — additive module, reuses existing pages */}
          <Route path="/paas/dashboard" element={<ProtectedRoute><PaasDashboard /></ProtectedRoute>} />
          <Route path="/paas/accounts" element={<ProtectedRoute><PaasAccounts /></ProtectedRoute>} />
          <Route path="/paas/onboard" element={<ProtectedRoute><PaasOnboard /></ProtectedRoute>} />
          <Route path="/paas/upload" element={<ProtectedRoute><BillJobs /></ProtectedRoute>} />
          <Route path="/paas/validations" element={<ProtectedRoute><ValidationReport /></ProtectedRoute>} />
          <Route path="/paas/missing" element={<ProtectedRoute><MissingBills /></ProtectedRoute>} />
          <Route path="/paas/demand" element={<ProtectedRoute><DemandReport /></ProtectedRoute>} />
          {/* Credits — Stripe top-up + usage/audit */}
          <Route path="/credits/success" element={<ProtectedRoute><CreditsSuccess /></ProtectedRoute>} />
          <Route path="/credits/cancel" element={<ProtectedRoute><CreditsCancel /></ProtectedRoute>} />
          <Route path="/settings/credits" element={<ProtectedRoute><CreditsUsagePage /></ProtectedRoute>} />
          {/* Sync Job Detail Route (opens in new tab) */}
          <Route
            path="/sync-job/:jobId"
            element={
              <ProtectedRoute>
                <SyncJobDetail />
              </ProtectedRoute>
            }
          />
          {/* Job Run Detail Route (opens in new tab) */}
          <Route
            path="/job-run/:runId"
            element={
              <ProtectedRoute>
                <JobRunDetail />
              </ProtectedRoute>
            }
          />
          {/* Payment Verification Routes (V1 - Legacy) */}
          <Route
            path="/payment-verification"
            element={
              <ProtectedRoute>
                <PaymentVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification/config"
            element={
              <ProtectedRoute>
                <PaymentConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification/:fileId"
            element={
              <ProtectedRoute>
                <PaymentFileView />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to={isAuthenticated() ? '/dashboard' : '/login'} />} />
          {/* Self-Serve foundation demo (Module 1) */}
          <Route
            path="/admin/_template-demo"
            element={
              <ProtectedRoute>
                <TemplateDemoPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        </PageLoadingBoundary>
        {/* AI Agent launcher now lives inside the top nav (see MainLayout.js → AiAgentNavLauncher) so it never overlaps drawers / modals. */}
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;
